import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

import { InputAutocompleteModel } from 'src/app/models/input-autocomplete.model';
import { PaginationModel } from 'src/app/models/pagination.model';
import { CompanyRoleResponse } from 'src/app/models/company.model';
import { ResponseObject } from 'src/app/models/response-object.model';
import { UserSaved } from 'src/app/models/user.model';

import { CompanyService } from 'src/app/services/company.service';
import { AuthenticationService } from '../../authentication/authentication/authentication.service';
import { AdminInvoiceService } from 'src/app/services/admin-invoice.service';

export interface FiscalDocumentCounterElement {
  companyId: number;
  companyName: string;
  invoicesTotal: number;
  cancelledTotal: number;
}

@Component({
  selector: 'app-fiscal-documents-count',
  templateUrl: './fiscal-documents-count.component.html',
  styleUrls: ['./fiscal-documents-count.component.scss']
})
export class FiscalDocumentsCountComponent implements OnInit {

  companies = [];
  allCompanies = [];

  inputCompany = new InputAutocompleteModel("", false, false, "", "", "", "", [], []);
  pagination = new PaginationModel(0, 5, 0, 0, [], []);

  timeout = null;
  selected: CompanyRoleResponse = null;

  startDate = "";
  endDate = "";
  filterWord = "";

  items: FiscalDocumentCounterElement[] = [];

  totalIssued = 0;
  totalCancelled = 0;
  totalDocuments = 0;

  user: UserSaved = null;

  loadingCompanies = false;
  loadingDocuments = false;

  sortField = 'total';
  sortDirection: 'asc' | 'desc' = 'desc';

  constructor(
    private authService: AuthenticationService,
    private adminInvoiceService: AdminInvoiceService,
    private companyService: CompanyService,
    private toastService: ToastrService
  ) {
    this.sortField = 'total';
    this.sortDirection = 'desc';
    this.companies = JSON.parse(this.authService.getCurrentUser()).companyRoles;
    this.allCompanies = this.companies;
    this.user = this.authService.getUser();

    const today = new Date();
    this.endDate = today.toLocaleDateString('en-CA');

    today.setDate(1);
    this.startDate = today.toLocaleDateString('en-CA');
  }

  ngOnInit(): void {
    this.getDocuments();

    if (this.user.contadorGT) {
      this.getAssociatedCompanies();
    }
  }

  getAssociatedCompanies() {
    this.loadingCompanies = true;

    this.companyService.getCompaniesAssociatedToPartner(this.user.id).subscribe({
      next: (response: ResponseObject) => {
        this.companies = response.data;
        this.allCompanies = this.companies;
      },
      error: () => {
        this.toastService.error("Error al obtener las compañías asociadas");
      },
      complete: () => {
        this.loadingCompanies = false;
      }
    });
  }

  getDocuments() {
    this.loadingDocuments = true;

    this.adminInvoiceService.getFiscalDocumentCounter(
      this.startDate,
      this.endDate
    ).subscribe({
      next: (response: ResponseObject) => {
        this.items = response.data as FiscalDocumentCounterElement[];
        this.calculateTotals();
        this.filterByWord();
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.toastService.error("Error al obtener los documentos fiscales");
        this.loadingDocuments = false;
      },
      complete: () => {
        this.loadingDocuments = false;
      }
    });
  }

  calculateTotals(items: FiscalDocumentCounterElement[] = this.items) {
    this.totalIssued = items.reduce((sum, item) => {
      return sum + (item.invoicesTotal || 0);
    }, 0);

    this.totalCancelled = items.reduce((sum, item) => {
      return sum + (item.cancelledTotal || 0);
    }, 0);

    this.totalDocuments = this.totalIssued + this.totalCancelled;
  }

  filterByWord() {
    const val = this.filterWord.toLowerCase();

    this.pagination.total = this.items.filter(item => {
      return !val
        || item.companyName?.toLowerCase().includes(val);
    });

    this.pagination.total.sort((a, b) => {

      let valueA: any;
      let valueB: any;

      switch (this.sortField) {
        case 'companyName':
          valueA = a.companyName || '';
          valueB = b.companyName || '';
          break;

        case 'invoicesTotal':
          valueA = a.invoicesTotal;
          valueB = b.invoicesTotal;
          break;

        case 'cancelledTotal':
          valueA = a.cancelledTotal;
          valueB = b.cancelledTotal;
          break;

        case 'total':
          valueA = a.invoicesTotal + a.cancelledTotal;
          valueB = b.invoicesTotal + b.cancelledTotal;
          break;
      }

      if (typeof valueA === 'string') {
        return this.sortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      return this.sortDirection === 'asc'
        ? valueA - valueB
        : valueB - valueA;
    });

    this.calculateTotals(this.pagination.total);
    this.resetPagination(this.pagination);
  }

  filter() {
    if (this.startDate && this.endDate) {
      this.getDocuments();
    }
  }

  searchCompany() {
    clearTimeout(this.timeout);
    this.inputCompany.class = "typing";
    this.selected = null;

    this.timeout = setTimeout(() => {
      const val = this.inputCompany.value.toLowerCase();

      this.companies = this.allCompanies.filter((company) => {
        return company.alphanumericId?.toLowerCase() === val
          || company.companyName?.toLowerCase().includes(val)
          || company.companyNIT?.toLowerCase().includes(val)
          || company.name?.toLowerCase().includes(val)
          || company.nit?.toLowerCase().includes(val);
      });

      this.inputCompany.verified = false;
      this.inputCompany.class = this.companies.length > 0 ? "fa-list" : "fa-xmark";

      if (!val) {
        this.selected = null;
        this.filter();
      }
    }, 300);
  }

  selectCompany(selected: CompanyRoleResponse) {
    clearTimeout(this.timeout);

    this.inputCompany.value =
      selected.alphanumericId + " - " +
      selected.companyNIT + " - " +
      selected.companyName;

    this.inputCompany.verified = true;
    this.inputCompany.class = "fa-circle-check";

    this.selected = selected;
    this.filter();
  }

  resetPagination(paginationTemp) {
    paginationTemp.startElement = 0;
    paginationTemp.pageIndex = 0;
    paginationTemp.lastElement = paginationTemp.pageSize;
    paginationTemp.filter = paginationTemp.total.slice(
      paginationTemp.startElement,
      paginationTemp.lastElement
    );
    paginationTemp.lastElement = paginationTemp.filter.length;
  }

  sortBy(field: string) {
    if (this.sortField === field) {
      this.sortDirection =
        this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'desc';
    }

    this.filterByWord();
  }
}